/**
 * Users API
 * RTK Query endpoints for user management (admin only)
 */

import type { User } from "../../types";
import { baseApi } from "./baseApi";

/**
 * User list response interface
 */
export interface UserListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: User[];
}

/**
 * Create user request interface
 */
export interface CreateUserData {
	email: string;
	first_name: string;
	last_name: string;
	phone: string;
	role: "admin" | "tenant" | "technician" | "agent";
	// Tenant-specific fields
	property_id?: string; // Property ID to assign tenant to
	lease_code?: string;
	property_address?: string;
	unit_number?: string;
	emergency_contact_name?: string;
	emergency_contact_phone?: string;
	// Lease creation fields (optional, only if property_id is provided)
	lease_start_date?: string; // ISO date string
	lease_end_date?: string; // ISO date string
	monthly_rent?: number;
	// Technician-specific fields
	primary_specialty?: string;
	experience?: string;
	license_number?: string;
	service_area?: string;
	additional_specialties?: string[];
	// Agent-specific fields
	brokerage_name?: string;
	service_areas?: string;
	property_types?: string[];
	// Admin-specific fields
	company_name?: string;
	job_title?: string;
	company_address?: string;
	property_count?: string;
}

/**
 * Create user response interface
 */
export interface CreateUserResponse {
	message: string;
	user: User;
	email_sent: boolean;
}

/**
 * Users API endpoints
 */
export const usersApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * List all users (admin only)
		 */
		listUsers: builder.query<
			UserListResponse,
			{
				page?: number;
				page_size?: number;
				role?: string;
				search?: string;
				ordering?: string;
			}
		>({
			query: (params = {}) => {
				const queryParams = new URLSearchParams();
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size)
					queryParams.append("page_size", params.page_size.toString());
				if (params.role) queryParams.append("role", params.role);
				if (params.search) queryParams.append("search", params.search);
				if (params.ordering) queryParams.append("ordering", params.ordering);

				const queryString = queryParams.toString();
				return {
					url: queryString ? `/users/?${queryString}` : `/users/`,
					method: "GET",
				};
			},
			providesTags: ["User"],
		}),

		/**
		 * List conversation participants (all authenticated users)
		 * Supports role and search filtering on backend
		 */
		listConversationParticipants: builder.query<
			UserListResponse,
			{
				page?: number;
				page_size?: number;
				role?: string;
				search?: string;
			}
		>({
			query: (params = {}) => {
				const queryParams = new URLSearchParams();
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size)
					queryParams.append("page_size", params.page_size.toString());
				if (params.role) queryParams.append("role", params.role);
				if (params.search) queryParams.append("search", params.search);

				const queryString = queryParams.toString();
				return {
					url: queryString
						? `/users/participants?${queryString}`
						: `/users/participants`,
					method: "GET",
				};
			},
			providesTags: ["User"],
		}),

		/**
		 * Get user by ID (admin only)
		 */
		getUser: builder.query<User, string>({
			query: (id) => ({
				url: `/users/${id}`,
				method: "GET",
			}),
			providesTags: (result, error, id) => [{ type: "User", id }],
		}),

		/**
		 * Create new user (admin only)
		 */
		createUser: builder.mutation<CreateUserResponse, CreateUserData>({
			query: (data) => ({
				url: "/users/",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["User"],
		}),

		/**
		 * Update user (admin only)
		 */
		updateUser: builder.mutation<
			User,
			{ id: string; data: Partial<CreateUserData> }
		>({
			query: ({ id, data }) => ({
				url: `/users/${id}`,
				method: "PATCH",
				body: data,
			}),
			invalidatesTags: (result, error, { id }) => [{ type: "User", id }],
		}),

		/**
		 * Delete user (admin only)
		 * Uses optimistic update for snappier UI
		 */
		deleteUser: builder.mutation<void, string>({
			query: (id) => ({
				url: `/users/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["User"],
			async onQueryStarted(id, { dispatch, queryFulfilled }) {
				// Optimistically remove user from all cached listUsers queries
				const patchResults: Array<() => void> = [];

				// We don't know all arg combinations in advance, so use util updateQueryData
				// for the generic "all users" case (most common: default filters)
				patchResults.push(
					dispatch(
						usersApi.util.updateQueryData(
							"listUsers",
							{ page: 1, page_size: 20 },
							(draft) => {
								if (!draft?.results) return;
								draft.results = draft.results.filter((user) => user.id !== id);
								draft.count = Math.max(0, draft.count - 1);
							},
						),
					).undo,
				);

				try {
					await queryFulfilled;
				} catch {
					// Roll back optimistic updates on failure
					patchResults.forEach((undo) => undo());
				}
			},
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useListUsersQuery,
	useLazyListUsersQuery,
	useListConversationParticipantsQuery,
	useGetUserQuery,
	useCreateUserMutation,
	useUpdateUserMutation,
	useDeleteUserMutation,
} = usersApi;
